/**
 * Prüftunnel: Ablauf und Ergebnisse.
 *
 * Der Vorschlag kommt bei jedem Schritt vom Server zurück. Absichtlich nicht
 * im Browser gerechnet: ob ein Messwert abweicht und welche Stufe daraus
 * folgt, entscheidet der Server – ein Client könnte eine Abweichung
 * stillschweigend übergehen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'
import { schluessel } from './abfragen'
import type { Stufe } from './typen'

export type Schrittart = 'hinweis' | 'sicht' | 'foto' | 'messwert' | 'bestaetigung'

export const SCHRITTARTTEXT: Record<Schrittart, string> = {
  hinweis: 'Hinweis',
  sicht: 'Sichtprüfung',
  foto: 'Foto',
  messwert: 'Messwert',
  bestaetigung: 'Bestätigung',
}

/**
 * Was jede Schrittart bewirkt.
 *
 * Steht in der Ablaufpflege neben der Auswahl. Ohne das ist der
 * Unterschied zwischen Sichtprüfung und Bestätigung nicht zu erraten — und
 * er ist erheblich: die eine schlägt eine Mängelstufe vor, die andere
 * nicht.
 */
export const SCHRITTARTHILFE: Record<Schrittart, { was: string; folge: string }> = {
  hinweis: {
    was: 'Nur zu lesen. Der Prüfer bestätigt, dass er es zur Kenntnis genommen hat.',
    folge: 'Ohne Wirkung auf die Beurteilung.',
  },
  sicht: {
    was: 'Der Prüfer beurteilt einen Zustand am Gerät: Verbindungen, Kanten, Verschleiß.',
    folge:
      '„Nicht in Ordnung“ wird als Mangel übernommen und schlägt Stufe 2 vor. ' +
      'Die Notiz landet im Bericht.',
  },
  foto: {
    was: 'Der Prüfer nimmt ein Bild auf. Es hängt am Befund und geht in den Bericht.',
    folge: 'Ohne Wirkung auf die Stufe.',
  },
  messwert: {
    was: 'Eine Zahl mit Sollbereich — Fallhöhe, Kettenstärke, Freiraum.',
    folge:
      'Liegt der Wert außerhalb, setzt die Anwendung die hinterlegte Stufe. ' +
      'Das ist die einzige Art, die eine Stufe verbindlich festlegt.',
  },
  bestaetigung: {
    was:
      'Eine Handlung des Prüfers, kein Zustand des Geräts: „Rundgang vollständig“, ' +
      '„Gerät nutzbar hinterlassen“, „Unterlagen lagen vor“.',
    folge:
      'Bleibt sie aus, erscheint das als offener Punkt in der Begründung und in ' +
      'der Bemerkung — aber ohne Mängelstufe. Ein fehlender Rundgang ist kein ' +
      'Mangel am Gerät.',
  },
}

export type Tunnelschritt = {
  id: string
  sortierung: number
  art: Schrittart
  titel: string
  hinweis: string | null
  pflicht: boolean
  messgroesse: string | null
  einheit: string | null
  sollwert_min: number | null
  sollwert_max: number | null
  stufe_bei_abweichung: Stufe | null
  normverweis: string | null
}

export type Tunnelvorlage = {
  id: string
  name: string
  geraetetyp: string | null
  /** Gilt für alle Geräte, deren Typ diese Norm trägt. */
  normteil: string | null
  beschreibung: string | null
  standard: boolean
  aktiv: boolean
  schritte: Tunnelschritt[]
}

export type Schrittergebnis = {
  id: string
  schritt_id: string
  erledigt_am: string | null
  in_ordnung: boolean | null
  messwert: number | null
  notiz: string | null
  abweichung: boolean
}

export type TunnelStand = {
  vorlage: Tunnelvorlage | null
  ergebnisse: Schrittergebnis[]
  schritte_gesamt: number
  schritte_erledigt: number
  pflicht_offen: number
  abweichungen: number
}

export type TunnelVorschlag = {
  stufe: Stufe
  mangel: string | null
  bemerkung: string | null
  normverweis: string | null
  begruendung: string[]
  vollstaendig: boolean
}

export type ErgebnisEingabe = {
  in_ordnung?: boolean | null
  messwert?: number | null
  notiz?: string | null
}

const tunnelSchluessel = (pruefungId: string, befundId: string) =>
  ['tunnel', pruefungId, befundId] as const

export function useTunnel(pruefungId: string, befundId: string, aktiv = true) {
  return useQuery({
    queryKey: tunnelSchluessel(pruefungId, befundId),
    queryFn: ({ signal }) =>
      api.holen<TunnelStand>(
        `/api/pruefungen/${pruefungId}/geraetebefunde/${befundId}/tunnel`,
        signal,
      ),
    enabled: aktiv,
    // Der Ablauf ändert sich während einer Prüfung nicht.
    staleTime: 5 * 60 * 1000,
  })
}

export function useVorschlag(pruefungId: string, befundId: string, aktiv = true) {
  return useQuery({
    queryKey: [...tunnelSchluessel(pruefungId, befundId), 'vorschlag'],
    queryFn: ({ signal }) =>
      api.holen<TunnelVorschlag>(
        `/api/pruefungen/${pruefungId}/geraetebefunde/${befundId}/tunnel/vorschlag`,
        signal,
      ),
    enabled: aktiv,
  })
}

export function useSchrittErledigen(pruefungId: string, befundId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ schrittId, eingabe }: { schrittId: string; eingabe: ErgebnisEingabe }) =>
      api.setzen<TunnelVorschlag>(
        `/api/pruefungen/${pruefungId}/geraetebefunde/${befundId}/tunnel/${schrittId}`,
        eingabe,
      ),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: tunnelSchluessel(pruefungId, befundId) })
    },
  })
}

export function useTunnelAbschliessen(pruefungId: string, befundId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      stufe?: Stufe | null
      mangel?: string | null
      bemerkung?: string | null
      normverweis?: string | null
    }) =>
      api.senden<TunnelVorschlag>(
        `/api/pruefungen/${pruefungId}/geraetebefunde/${befundId}/tunnel/abschliessen`,
        daten,
      ),
    onSuccess: () => {
      speicher.invalidateQueries({ queryKey: tunnelSchluessel(pruefungId, befundId) })
      speicher.invalidateQueries({ queryKey: schluessel.bogen(pruefungId) })
    },
  })
}

/** Sollbereich als Text, für die Anzeige am Messschritt. */
export function sollbereichText(schritt: Tunnelschritt): string {
  const einheit = schritt.einheit ? ` ${schritt.einheit}` : ''
  const teile: string[] = []
  if (schritt.sollwert_min !== null) teile.push(`mindestens ${zahl(schritt.sollwert_min)}${einheit}`)
  if (schritt.sollwert_max !== null) teile.push(`höchstens ${zahl(schritt.sollwert_max)}${einheit}`)
  return teile.join(' und ')
}

/** Zahl mit Komma, ohne überflüssige Nullen. */
export function zahl(wert: number): string {
  return String(wert).replace(/\.0+$/, '').replace('.', ',')
}

/** Liegt der eingegebene Wert außerhalb? Nur für die sofortige Anzeige –
 *  verbindlich rechnet der Server. */
export function weichtAb(schritt: Tunnelschritt, wert: number | null): boolean {
  if (wert === null || Number.isNaN(wert)) return false
  if (schritt.sollwert_min !== null && wert < schritt.sollwert_min) return true
  if (schritt.sollwert_max !== null && wert > schritt.sollwert_max) return true
  return false
}

// ── Vorlagen pflegen ─────────────────────────────────────────────
//
// Die Prüfabläufe sind Bernds fachliches Kapital: was bei einer Schaukel in
// welcher Reihenfolge gemessen wird, steckt hier drin. Deshalb muss er sie
// selbst ändern können, ohne uns.

export function useTunnelvorlagen() {
  return useQuery({
    queryKey: ['tunnelvorlagen'] as const,
    queryFn: ({ signal }) =>
      api.holen<Tunnelvorlage[]>('/api/kataloge/tunnelvorlagen', signal),
  })
}

function vorlagenNeuLaden(speicher: ReturnType<typeof useQueryClient>) {
  return speicher.invalidateQueries({ queryKey: ['tunnelvorlagen'] })
}

export function useVorlageAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: {
      name: string
      geraetetyp?: string
      normteil?: string
      beschreibung?: string
    }) =>
      api.senden<Tunnelvorlage>('/api/kataloge/tunnelvorlagen', daten),
    onSuccess: () => vorlagenNeuLaden(speicher),
  })
}

export function useVorlageAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Record<string, unknown>) =>
      api.aendern<Tunnelvorlage>(`/api/kataloge/tunnelvorlagen/${id}`, daten),
    onSuccess: () => vorlagenNeuLaden(speicher),
  })
}

export function useSchrittAnlegen(vorlageId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (daten: Record<string, unknown>) =>
      api.senden(`/api/kataloge/tunnelvorlagen/${vorlageId}/schritte`, daten),
    onSuccess: () => vorlagenNeuLaden(speicher),
  })
}

export function useSchrittAendern() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...daten }: { id: string } & Record<string, unknown>) =>
      api.aendern(`/api/kataloge/tunnelschritte/${id}`, daten),
    onSuccess: () => vorlagenNeuLaden(speicher),
  })
}

export function useSchrittLoeschen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kataloge/tunnelschritte/${id}`),
    onSuccess: () => vorlagenNeuLaden(speicher),
  })
}
